/**
 * Pure H OS · Phase D Wave 2 · PMO.
 * Cross-org project portfolio with phase, health, budget, risks.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { PMO_PROJECTS, PmoProject, PmoPhase, PmoHealth } from "../mocks/pmoData";

const PHASE_LABEL: Record<PmoPhase, string> = {
  kickoff: "Kickoff", discovery: "Discovery", delivery: "Delivery",
  launch: "Launch", closed: "Closed",
};
function healthSeverity(h: PmoHealth): "low" | "medium" | "high" {
  return h === "green" ? "low" : h === "amber" ? "medium" : "high";
}

type Tab = "all" | "at-risk" | PmoPhase;

export default function PMO() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return PMO_PROJECTS;
    if (tab === "at-risk") return PMO_PROJECTS.filter((p) => p.health !== "green");
    return PMO_PROJECTS.filter((p) => p.phase === tab);
  }, [tab]);

  const current = openId ? PMO_PROJECTS.find((p) => p.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="pmo-page">
      <Card testId="pmo-filters">
        <TabBar<Tab>
          testId="pmo-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",       label: `All ${PMO_PROJECTS.length}` },
            { key: "at-risk",   label: "At risk" },
            { key: "kickoff",   label: "Kickoff" },
            { key: "discovery", label: "Discovery" },
            { key: "delivery",  label: "Delivery" },
            { key: "launch",    label: "Launch" },
            { key: "closed",    label: "Closed" },
          ]}
        />
      </Card>

      <Card padded={false} testId="pmo-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Project Portfolio" title={`${filtered.length} project${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
            Every cross-org project — phase, health, progress, budget.
          </div>
        </div>
        <div className="p-2">
          {filtered.map((p) => (
            <ListRow key={p.id} testId={`pmo-${p.id}`}
              onClick={() => setOpenId(p.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>
                    {PHASE_LABEL[p.phase]}
                  </span>
                  <span>{p.name}</span>
                </span>
              }
              sublabel={<>{p.lead} · {p.progressPct}% · budget EGP {p.budgetEGP.toLocaleString()} · due {p.dueRelative}</>}
              trailing={<SeverityBadge severity={healthSeverity(p.health)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="pmo-drawer" title={current ? current.name : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${PHASE_LABEL[current.phase]} · ${current.lead}`} title={`Sponsor · ${current.sponsor}`} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Progress</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.progressPct}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Health</div>
                <div className="mt-2"><SeverityBadge severity={healthSeverity(current.health)} /></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Milestones</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.milestonesDone} / {current.milestones}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Risks</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.risks}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Budget</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.budgetEGP.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Spent</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {current.spendEGP.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Started</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.startedRelative}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Due</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.dueRelative}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
